import type { Query, QueryCache, QueryOptions } from "@tanstack/react-query";
import { ApiError, type FetchGateResult } from "@workspace/api-client-react";
import { toast } from "@/hooks/use-toast";

type RefetchInterval = number | false | undefined;
type RefetchAwareOptions = QueryOptions<unknown, Error, unknown, readonly unknown[]> & {
  refetchInterval?: RefetchInterval;
};

export interface ThrottleInfo {
  retryAfterSec: number;
  area: string;
}

export function isThrottleError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 429;
}

export function getRetryAfterSec(error: ApiError): number {
  const fromBody =
    error.data && typeof error.data === "object"
      ? (error.data as { retryAfterSec?: unknown }).retryAfterSec
      : undefined;
  if (typeof fromBody === "number" && Number.isFinite(fromBody) && fromBody > 0) {
    return Math.ceil(fromBody);
  }
  const header = error.headers.get("retry-after");
  if (header) {
    const n = Number.parseInt(header, 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 30;
}

const PATH_TO_AREA: Array<{ test: RegExp; label: string }> = [
  { test: /\/api\/reports\/period-comparison/, label: "Period comparison report" },
  { test: /\/api\/reports\/spend-vs-profit/, label: "Spend vs profit report" },
  { test: /\/api\/reports\/profit-loss/, label: "Profit & loss report" },
  { test: /\/api\/reports\/n\/daily/, label: "Sales performance (daily)" },
  { test: /\/api\/reports\/n\/by-channel/, label: "Sales performance (by channel)" },
  { test: /\/api\/reports\/n\/sessions/, label: "Sales performance (sessions)" },
  { test: /\/api\/reports\/n/, label: "SKU-by-channel report" },
  { test: /\/api\/admin\/ebay-sync-issues/, label: "eBay sync issues" },
  { test: /\/api\/admin\/consignment-rebalance/, label: "Consignment rebalance" },
  { test: /\/api\/reports\//, label: "Report" },
  { test: /\/api\/admin\//, label: "Admin tool" },
];

function extractPath(error: ApiError, queryKey?: readonly unknown[]): string {
  if (error.url) return error.url;
  if (queryKey && queryKey.length > 0 && typeof queryKey[0] === "string") {
    return queryKey[0];
  }
  return "";
}

export function describeArea(error: ApiError, queryKey?: readonly unknown[]): string {
  const path = extractPath(error, queryKey);
  for (const entry of PATH_TO_AREA) {
    if (entry.test.test(path)) return entry.label;
  }
  return "This page";
}

// ---------------------------------------------------------------------------
// Deduped toasts: one toast per (area + active cooldown). Subsequent 429s
// for the same area while still throttled update the existing toast's
// countdown rather than stacking new ones.
// ---------------------------------------------------------------------------

interface ActiveToast {
  dismiss: () => void;
  update: (props: { title: string; description: string }) => void;
  expiresAt: number;
  intervalId: ReturnType<typeof setInterval>;
}

const activeToasts = new Map<string, ActiveToast>();

function formatCountdown(sec: number): string {
  if (sec <= 0) return "now";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

export function showThrottleToast(area: string, retryAfterSec: number): void {
  const now = Date.now();
  const expiresAt = now + retryAfterSec * 1000;
  const existing = activeToasts.get(area);

  const description = `Too many requests. Try again in ${formatCountdown(retryAfterSec)}.`;

  if (existing && existing.expiresAt > now) {
    existing.expiresAt = Math.max(existing.expiresAt, expiresAt);
    existing.update({ title: `${area} is busy`, description });
    return;
  }

  if (existing) {
    clearInterval(existing.intervalId);
    activeToasts.delete(area);
  }

  const t = toast({
    title: `${area} is busy`,
    description,
    variant: "destructive",
    duration: retryAfterSec * 1000 + 500,
  });

  const entry: ActiveToast = {
    dismiss: t.dismiss,
    update: ({ title, description: desc }) =>
      t.update({
        id: t.id,
        title,
        description: desc,
        variant: "destructive",
        open: true,
      }),
    expiresAt,
    intervalId: setInterval(() => {
      const remaining = Math.ceil((entry.expiresAt - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(entry.intervalId);
        activeToasts.delete(area);
        entry.dismiss();
        return;
      }
      entry.update({
        title: `${area} is busy`,
        description: `Too many requests. Try again in ${formatCountdown(remaining)}.`,
      });
    }, 1000),
  };
  activeToasts.set(area, entry);
}

// ---------------------------------------------------------------------------
// Route-level cooldowns (Task #1917)
//
// One 429 means the WHOLE route is throttled for this user, not just the one
// query that happened to trip it. We remember the cooldown per route path so:
//   1. `throttleFetchGate` (wired into customFetch via `setFetchGate`) blocks
//      NEW fetches to the route client-side until Retry-After expires — other
//      widgets on the page stop hammering a route that will only 429.
//   2. `handleQueryErrorForThrottle` pauses the polling interval of EVERY
//      cached query on that route, not just the failing one.
// ---------------------------------------------------------------------------

/** pathname -> cooldown expiry (epoch ms). */
const routeCooldowns = new Map<string, number>();

/** Normalize an absolute or relative request URL to its pathname. */
export function routeKeyFromPath(path: string): string {
  let p = path;
  if (/^https?:\/\//i.test(p)) {
    try {
      p = new URL(p).pathname;
    } catch {
      // fall through with the raw string
    }
  }
  const q = p.indexOf("?");
  return q === -1 ? p : p.slice(0, q);
}

export function registerRouteCooldown(path: string, retryAfterSec: number): void {
  const key = routeKeyFromPath(path);
  if (!key) return;
  const expiresAt = Date.now() + retryAfterSec * 1000;
  const existing = routeCooldowns.get(key);
  if (!existing || expiresAt > existing) routeCooldowns.set(key, expiresAt);
}

/** Seconds remaining in the route's cooldown, or 0 when not throttled. */
export function getRouteCooldownRemainingSec(path: string): number {
  const key = routeKeyFromPath(path);
  const expiresAt = routeCooldowns.get(key);
  if (!expiresAt) return 0;
  const remainingMs = expiresAt - Date.now();
  if (remainingMs <= 0) {
    routeCooldowns.delete(key);
    return 0;
  }
  return Math.ceil(remainingMs / 1000);
}

/**
 * Fetch gate for `setFetchGate` on the shared api client: blocks GETs to
 * routes with an active 429 cooldown with a synthetic 429 (no network hit).
 */
export function throttleFetchGate(url: string): FetchGateResult {
  const remaining = getRouteCooldownRemainingSec(url);
  if (remaining > 0) return { blocked: true, retryAfterSec: remaining };
  return { blocked: false };
}

// The QueryCache is registered once at app bootstrap so a 429 on one query
// can pause the refetch intervals of every sibling query on the same route.
let registeredQueryCache: QueryCache | null = null;

export function setThrottleQueryCache(cache: QueryCache | null): void {
  registeredQueryCache = cache;
}

function queryRoutePath(query: Query<unknown, unknown, unknown, readonly unknown[]>): string {
  const first = query.queryKey[0];
  return typeof first === "string" ? routeKeyFromPath(first) : "";
}

// ---------------------------------------------------------------------------
// User-initiated vs background throttles (Task #1917)
//
// A destructive toast is only warranted when the operator did something and
// got throttled (clicked Refresh, changed filters) or when the page has
// nothing to show. A background poll that trips the limiter while the page
// already displays data should back off silently — the pause/gate above
// already stops the hammering, and the data on screen is still fine.
//
// Heuristic for "user-initiated": a pointer/key interaction within the last
// few seconds. Filter changes produce a NEW query key with no cached data,
// so they are also covered by the "nothing to show" branch.
// ---------------------------------------------------------------------------

const USER_INITIATED_WINDOW_MS = 5_000;
let lastInteractionAt = 0;

/** Record a user interaction (installed globally; exported for tests). */
export function markUserInteraction(): void {
  lastInteractionAt = Date.now();
}

if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", markUserInteraction, { capture: true, passive: true });
  window.addEventListener("keydown", markUserInteraction, { capture: true, passive: true });
}

/**
 * True when a pointer/key interaction happened within the last few seconds —
 * used to distinguish "the operator clicked something and got throttled"
 * (worth a toast) from a background poll tripping the limiter (silent).
 */
export function wasRecentUserInteraction(): boolean {
  return Date.now() - lastInteractionAt < USER_INITIATED_WINDOW_MS;
}

// ---------------------------------------------------------------------------
// React Query integration
// ---------------------------------------------------------------------------

/**
 * Called from the global QueryCache `onError`. When a query trips the
 * per-user rate limiter on the API server (HTTP 429):
 *   1. Surface a friendly, deduped toast naming the throttled area and a
 *      live countdown to when the operator can try again.
 *   2. If the query is on a polling `refetchInterval`, pause it until
 *      after `Retry-After` so we stop hammering the throttled endpoint.
 */
export function handleQueryErrorForThrottle(
  error: unknown,
  query: Query<unknown, unknown, unknown, readonly unknown[]>,
): void {
  if (!isThrottleError(error)) return;
  const retryAfterSec = getRetryAfterSec(error);

  // Route-level cooldown: block new fetches to this route (via the fetch
  // gate) and pause every cached query polling the same route — one 429
  // means the whole route is throttled for this user.
  const routePath = routeKeyFromPath(extractPath(error, query.queryKey));
  if (routePath) registerRouteCooldown(routePath, retryAfterSec);

  pauseQueryRefetchInterval(query, retryAfterSec);
  if (routePath && registeredQueryCache) {
    for (const other of registeredQueryCache.getAll()) {
      if (other !== query && queryRoutePath(other) === routePath) {
        pauseQueryRefetchInterval(other, retryAfterSec);
      }
    }
  }

  // Only alarm the operator when they did something (recent interaction) or
  // the page has nothing to show; background refetch throttles with data on
  // screen back off silently.
  const hasDataToShow = query.state.data !== undefined;
  if (wasRecentUserInteraction() || !hasDataToShow) {
    const area = describeArea(error, query.queryKey);
    showThrottleToast(area, retryAfterSec);
  }
}

// Per-query pause bookkeeping. When a query is throttled while still
// paused from a previous 429, we extend the pause to the latest
// retryAfterSec (and never accidentally restore early to the original
// interval). Keyed by Query identity via WeakMap so entries clean up
// when React Query garbage-collects the query.
interface PauseState {
  original: number;
  restoreAt: number;
  timer: ReturnType<typeof setTimeout>;
}
const paused = new WeakMap<
  Query<unknown, unknown, unknown, readonly unknown[]>,
  PauseState
>();

function pauseQueryRefetchInterval(
  query: Query<unknown, unknown, unknown, readonly unknown[]>,
  retryAfterSec: number,
): void {
  const restoreAt = Date.now() + retryAfterSec * 1000 + 250;
  const existing = paused.get(query);

  if (existing) {
    if (restoreAt <= existing.restoreAt) return;
    clearTimeout(existing.timer);
    existing.restoreAt = restoreAt;
    existing.timer = scheduleRestore(query, existing, restoreAt - Date.now());
    return;
  }

  const opts = query.options as RefetchAwareOptions;
  const original = opts.refetchInterval;
  if (original === undefined || original === false || typeof original !== "number") {
    return;
  }

  query.setOptions({ ...opts, refetchInterval: false } as RefetchAwareOptions);
  const state: PauseState = {
    original,
    restoreAt,
    timer: undefined as unknown as ReturnType<typeof setTimeout>,
  };
  state.timer = scheduleRestore(query, state, restoreAt - Date.now());
  paused.set(query, state);
}

function scheduleRestore(
  query: Query<unknown, unknown, unknown, readonly unknown[]>,
  state: PauseState,
  delayMs: number,
): ReturnType<typeof setTimeout> {
  return setTimeout(() => {
    paused.delete(query);
    const current = (query.options as RefetchAwareOptions).refetchInterval;
    // Restore only if we still own the paused state (refetchInterval
    // is false) — a consumer remount/setOptions may have replaced it.
    if (current === false) {
      query.setOptions({
        ...(query.options as RefetchAwareOptions),
        refetchInterval: state.original,
      } as RefetchAwareOptions);
    }
  }, Math.max(0, delayMs));
}

/** Test hook: clear all live throttle toasts. */
export function __resetThrottleToastsForTests(): void {
  for (const entry of activeToasts.values()) {
    clearInterval(entry.intervalId);
    entry.dismiss();
  }
  activeToasts.clear();
}

/** Test hook: clear route cooldowns + interaction/queryCache state. */
export function __resetThrottleRouteStateForTests(): void {
  routeCooldowns.clear();
  lastInteractionAt = 0;
  registeredQueryCache = null;
}
